/**
 * Strips query parameters from URLs.
 * Admin-entered URLs may have ?auto_hide=true which must be removed
 * before video type detection.
 */
export const stripQueryParams = (url) => {
  if (!url || typeof url !== "string") return url;
  return url.split("?")[0];
};

/**
 * Normalizes URLs to proper format for storage.
 * - Removes query parameters
 * - Ensures MP4s use video.isilive.ca domain
 * - Ensures HLS streams use proper path structure
 */
export const normalizeVideoUrl = (url) => {
  if (!url || typeof url !== "string") return url;

  // First, strip query parameters
  let cleanUrl = stripQueryParams(url);

  // Handle linx URLs with query params that have /index.m3u8 appended incorrectly
  // e.g., "...mp4?auto_hide=true/index.m3u8" -> "...mp4"
  if (cleanUrl.includes(".mp4") && cleanUrl.includes("/index.m3u8")) {
    cleanUrl = cleanUrl.split(".mp4")[0] + ".mp4";
  }

  // Convert linx.isilive.ca MP4s to video.isilive.ca
  if (cleanUrl.includes("linx.isilive.ca") && cleanUrl.includes(".mp4")) {
    // Extract just the path after /live/
    const pathMatch = cleanUrl.match(/\/live\/(.+\.mp4)/);
    if (pathMatch) {
      cleanUrl = `https://video.isilive.ca/${pathMatch[1]}`;
    }
  }

  return cleanUrl;
};

/**
 * Sanitizes URLs for Roku compatibility.
 * Roku only accepts clean .mp4 or .m3u8 URLs without query parameters.
 *
 * Rules:
 * - Strip query parameters from all URLs
 * - Don't append /index.m3u8 to .mp4 URLs
 * - Don't append /index.m3u8 if URL already contains /index.m3u8
 * - Remove malformed suffixes like /index.m3u8 after query parameters
 */

export const sanitizeRokuUrl = (url) => {
  if (!url || typeof url !== "string") return url;

  // Remove query parameters
  let cleanUrl = url.split("?")[0];

  // Handle case where /index.m3u8 was incorrectly appended after query params
  // e.g., "...mp4?auto_hide=true/index.m3u8" -> "...mp4"
  if (cleanUrl.includes(".mp4") && cleanUrl.includes("/index.m3u8")) {
    // Keep only the mp4 part
    cleanUrl = cleanUrl.split(".mp4")[0] + ".mp4";
  }

  // if URL ends with mp4, don't append anything
  if (cleanUrl.endsWith(".mp4")) {
    return cleanUrl;
  }

  // If URL ends with /index.m3u8, keep it as is
  if (cleanUrl.endsWith("/index.m3u8")) {
    return cleanUrl;
  }

  // If URL is a linx stream path without /index.m3u8, append it
  if (
    cleanUrl.includes("linx.isilive.ca/live/") &&
    !cleanUrl.endsWith("/index.m3u8")
  ) {
    return cleanUrl.endsWith("/")
      ? cleanUrl + "index.m3u8"
      : cleanUrl + "/index.m3u8";
  }

  return cleanUrl;
};

/**
 * Validates if a URL is acceptable for Roku
 */
export const isValidRokuUrl = (url) => {
  if (!url || typeof url !== "string") return false;

  // Must be http(s)
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return false;
  }

  // Must be mp4 or m3u8
  if (!url.includes(".mp4") && !url.includes(".m3u8")) {
    return false;
  }

  // Must not have query parameters
  if (url.includes("?")) {
    return false;
  }

  // Must not have malformed paths like mp4/index.m3u8
  if (url.includes(".mp4/") || url.includes(".mp4%2F")) {
    return false;
  }

  return true;
};

/**
 * Gets the proper embed/stream URL for Roku
 * @param {string} url - The raw video URL
 * @returns {string} - Sanitized URL safe for Roku
 */
export const getRokuUrl = (url) => {
  return sanitizeRokuUrl(url);
};
